import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./StringingDiscounts.module.css";
import useAuthGuard from "../../../hooks/useAuthGuard";
import {
  getDiscountsApi,
  createDiscountApi,
  updateDiscountApi,
  deleteDiscountApi,
} from "../../../api/api";

const StringingDiscounts = () => {
  useAuthGuard();
  const [role, setRole] = useState("");
  const [discounts, setDiscounts] = useState([]);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Form State
  const [formData, setFormData] = useState({
    discount_name: "",
    start_date: "",
    end_date: "",
    regular_price: "",
    express_price: "",
  });

  const [editingId, setEditingId] = useState(null);

  const fetchDiscounts = async () => {
    const token = localStorage.getItem("adminToken");
    try {
      const response = await getDiscountsApi(token);
      setDiscounts(response.data);
    } catch (e) {
      console.error("Error fetching discounts:", e);
      showMsg("error", "Failed to load discounts.");
    }
  };

  useEffect(() => {
    const r = localStorage.getItem("adminRole");
    setRole(r || "");
    if (r === "superadmin") {
      fetchDiscounts();
    }
  }, []);

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  if (role && role !== "superadmin") {
    return (
      <div className={styles.full}>
        <header className={styles.header}>
          <h2 className={styles.pageHeading}>Access Denied</h2>
        </header>
        <div className={styles.main}>
          <div className={styles.card}>
            <p>You must be a Super Admin to manage stringing discounts.</p>
            <button className={styles.goback} onClick={() => navigate(-1)}>
              &larr; Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (discount) => {
    setEditingId(discount.id);
    
    // Format dates to YYYY-MM-DD for input elements
    const startStr = discount.start_date ? new Date(discount.start_date).toISOString().split("T")[0] : "";
    const endStr = discount.end_date ? new Date(discount.end_date).toISOString().split("T")[0] : "";

    setFormData({
      discount_name: discount.discount_name,
      start_date: startStr,
      end_date: endStr,
      regular_price: discount.regular_price,
      express_price: discount.express_price,
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({
      discount_name: "",
      start_date: "",
      end_date: "",
      regular_price: "",
      express_price: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("adminToken");

    if (
      !formData.discount_name ||
      !formData.start_date ||
      !formData.end_date ||
      formData.regular_price === "" ||
      formData.express_price === ""
    ) {
      showMsg("error", "All fields are required.");
      return;
    }

    if (new Date(formData.start_date) > new Date(formData.end_date)) {
      showMsg("error", "Start date cannot be after end date.");
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingId) {
        await updateDiscountApi(editingId, formData, token);
        showMsg("success", "Discount updated successfully.");
      } else {
        await createDiscountApi(formData, token);
        showMsg("success", "Discount created successfully.");
      }
      handleCancel();
      fetchDiscounts();
    } catch (error) {
      console.error(error);
      const errorMsg = error?.response?.data?.error || "Operation failed.";
      showMsg("error", errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this discount configuration?")) {
      return;
    }

    const token = localStorage.getItem("adminToken");
    try {
      await deleteDiscountApi(id, token);
      showMsg("success", "Discount deleted successfully.");
      fetchDiscounts();
    } catch (error) {
      console.error(error);
      showMsg("error", "Failed to delete discount.");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Manage Stringing Discounts</h1>
      </div>

      <div className={styles.content}>
        {msg.text && (
          <div
            className={
              msg.type === "success" ? styles.successMessage : styles.errorMessage
            }
          >
            {msg.text}
          </div>
        )}

        <div className={styles.gridContainer}>
          {/* Discount Form Card */}
          <div className={styles.card}>
            <h3>{editingId ? "Edit Discount Period" : "Create New Discount Period"}</h3>
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label>Discount/Event Name</label>
                <input
                  type="text"
                  name="discount_name"
                  value={formData.discount_name}
                  onChange={handleInputChange}
                  placeholder="e.g., Summer Discount 2026"
                  required
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Start Date</label>
                  <input
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>End Date</label>
                  <input
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Regular Stringing Discounted Price (AED)</label>
                  <input
                    type="number"
                    name="regular_price"
                    value={formData.regular_price}
                    onChange={handleInputChange}
                    placeholder="e.g., 49"
                    min="0"
                    step="0.01"
                    required
                  />
                  <small className={styles.hint}>Normal price is AED 75</small>
                </div>
                <div className={styles.formGroup}>
                  <label>Express Stringing Discounted Price (AED)</label>
                  <input
                    type="number"
                    name="express_price"
                    value={formData.express_price}
                    onChange={handleInputChange}
                    placeholder="e.g., 69"
                    min="0"
                    step="0.01"
                    required
                  />
                  <small className={styles.hint}>Normal price is AED 100</small>
                </div>
              </div>

              <div className={styles.formActions}>
                <button
                  type="submit"
                  className={styles.submitBtn}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Processing..." : editingId ? "Save Changes" : "Create Discount"}
                </button>
                {editingId && (
                  <button
                    type="button"
                    onClick={handleCancel}
                    className={styles.cancelBtn}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Discounts List Card */}
          <div className={styles.listCard}>
            <h3>Configured Discount Periods</h3>
            <div className={styles.tableResponsive}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Event Name</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Regular Price</th>
                    <th>Express Price</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {discounts.length > 0 ? (
                    discounts.map((discount) => {
                      const startDateStr = new Date(discount.start_date).toLocaleDateString();
                      const endDateStr = new Date(discount.end_date).toLocaleDateString();
                      const isCurrentlyActive =
                        new Date() >= new Date(discount.start_date) &&
                        new Date(new Date().setHours(0, 0, 0, 0)) <= new Date(discount.end_date);

                      return (
                        <tr key={discount.id} className={isCurrentlyActive ? styles.activeRow : ""}>
                          <td>
                            <strong>{discount.discount_name}</strong>
                            {isCurrentlyActive && (
                              <span className={styles.badgeActive}>Active</span>
                            )}
                          </td>
                          <td>{startDateStr}</td>
                          <td>{endDateStr}</td>
                          <td>AED {discount.regular_price}</td>
                          <td>AED {discount.express_price}</td>
                          <td>
                            <button
                              className={styles.editBtn}
                              onClick={() => handleEdit(discount)}
                            >
                              Edit
                            </button>
                            <button
                              className={styles.deleteBtn}
                              onClick={() => handleDelete(discount.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className={styles.empty}>
                        No discount periods configured.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StringingDiscounts;
